import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import { font, welcomeScreenUI } from "../../../../configs";
import { GENDERS } from "../../../../constants/modal";
import {
  capitalizeFirstLetter,
  isWithArrow,
  useIsMobile,
} from "../../../../utils";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { handleAnalytics } from "../../../../utils/tracking";
import GenderButton from "./GenderButton";

import "./index.css";

interface GenderBlockProps {
  selectedGender: string;
  handleGenderSelect: (newUnit: string) => void;
  nextStep: () => void;
  genderMaleKey: "gender.children.male" | "gender.male";
  genderFemaleKey: "gender.children.female" | "gender.female";
  onGenderSelected?: () => void;
}

type GenderButtonType = "focused" | "unfocused";

const GenderBlock: React.FC<GenderBlockProps> = ({
  selectedGender,
  handleGenderSelect,
  nextStep,
  genderMaleKey,
  genderFemaleKey,
  onGenderSelected,
}) => {
  const isMobile = useIsMobile();
  const isLargeHeightRelative = useMediaQuery({
    maxHeight: !isMobile ? 830 : 778,
    minWidth: 767,
  });

  const [hovered, setHovered] = useState<string | null>(null);
  const { t } = useTranslation("components/intro");

  const brandDefined = findBrandByDomain();

  const isVelourGarments = brandDefined?.name === "Velour Garments";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isMarcyParis = brandDefined?.name === "Marcy Paris";
  const isSportyRich = brandDefined?.name === "Sporty & Rich";
  const isTDF = brandDefined?.name === "TDF";

  const isMobileTDF = isTDF && isMobile;

  const isGivenchyMobile = isGivenchy && isMobile;

  const getButtonsStyles = (type: GenderButtonType) => ({
    backgroundColor: welcomeScreenUI.genderCTA[type].backgroundColor,
    borderRadius: welcomeScreenUI.genderCTA[type].borderRadius,
    borderColor: welcomeScreenUI.genderCTA[type].borderColor,
    borderWidth: welcomeScreenUI.genderCTA[type].borderWidth,
    fontSize: welcomeScreenUI.genderCTA[type].fontSize,
    fontWeight: welcomeScreenUI.genderCTA[type].fontWeight,
    textTransform:
      brandDefined?.name === "Lacoste" && isMobile
        ? "uppercase"
        : (welcomeScreenUI.genderCTA[type]
            .textTransform as React.CSSProperties["textTransform"]),
    color: welcomeScreenUI.genderCTA[type].fontColor,
    fontFamily: `${font}, sans-serif${isMobile ? "" : " !important"}`,
    letterSpacing: (welcomeScreenUI.genderCTA[type] as any)?.letterSpacing,
    width: !isMobile && (isGivenchy || isMarcyParis) ? "50%" : "auto",
    display: "flex",
    flexDirection: isMobileTDF
      ? "row"
      : !isMobileTDF && isMobile
        ? "column"
        : ("row" as React.CSSProperties["flexDirection"]),
  });

  const femaleButtonStyles =
    selectedGender === GENDERS.F
      ? getButtonsStyles("focused")
      : getButtonsStyles("unfocused");

  const maleButtonStyles =
    selectedGender === GENDERS.M
      ? getButtonsStyles("focused")
      : getButtonsStyles("unfocused");

  const getTabIndex = (_gender: string) => {
    return 0;
  };

  const renderGenderButtons = () => {
    const maleButton = (
      <GenderButton
        key="male"
        gender={GENDERS.M}
        tabIndex={getTabIndex(GENDERS.M)}
        selectedGender={selectedGender}
        baseStyles={maleButtonStyles}
        hovered={hovered}
        setHovered={setHovered}
        label={
          maleButtonStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t(genderMaleKey))
            : t(genderMaleKey)
        }
        onSelect={() => {
          handleGenderSelect(GENDERS.M);
          if (isMobile && !isGivenchy) nextStep();
          onGenderSelected?.();
          handleAnalytics("action", "gender_selection", {
            key: "value",
            value: GENDERS.M,
          });
        }}
      />
    );

    const femaleButton = (
      <GenderButton
        key="female"
        gender={GENDERS.F}
        tabIndex={getTabIndex(GENDERS.F)}
        selectedGender={selectedGender}
        baseStyles={femaleButtonStyles}
        hovered={hovered}
        setHovered={setHovered}
        label={
          femaleButtonStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t(genderFemaleKey))
            : t(genderFemaleKey)
        }
        onSelect={() => {
          handleGenderSelect(GENDERS.F);
          if (isMobile && !isGivenchy) nextStep();
          onGenderSelected?.();
          handleAnalytics("action", "gender_selection", {
            key: "value",
            value: GENDERS.F,
          });
        }}
      />
    );

    if (isVelourGarments) {
      return [maleButton, femaleButton];
    } else {
      return [femaleButton, maleButton];
    }
  };

  return (
    <div
      className="gender-buttons"
      style={{
        marginBottom: isGivenchyMobile
          ? "20px"
          : isLargeHeightRelative && !isSportyRich
            ? 0
            : "30px",
        justifyContent:
          isGivenchy && !isMobile
            ? "space-between"
            : isWithArrow(brandDefined?.name)
              ? "center"
              : "",
        gap: isGivenchy ? "10px" : "20px",
        display: "flex",
        flexDirection: isMobileTDF
          ? "row"
          : isMobile && !isTDF
            ? "column"
            : "row",
      }}
    >
      {renderGenderButtons()}
    </div>
  );
};

export default GenderBlock;
